import {
  formatDateToISTIsoString,
  formatDateToIsoString,
  getFileFormat,
  getMediaExtension,
} from '@/utils/helper'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import {
  CreateInventory,
  CreateStore,
  LanguageState,
  Product,
  ProductData,
  StoreSessionDay,
} from '@a4b/api/src/modules/Monetisation/Puja/types'

import { CreateInventoryPayload } from '../../types'
import { getStatesByLanguage } from './constants'
import { DEFAULT_BATCH_SIZE } from '@/modules/monetisation/offerings/CreateTemple/utils/constants'

const convertToM3U8Url = (url: string) => {
  if (!url) return ''

  let updatedUrl = url

  // Replace domain accordingly
  if (url.includes('srm-sbox-cdn.a4b.io')) {
    updatedUrl = url.replace('srm-sbox-cdn.a4b.io', 'srm-sbox-cdn-g.a4b.io')
  } else if (url.includes('srm-cdn.a4b.io')) {
    updatedUrl = url.replace('srm-cdn.a4b.io', 'srm-cdn-g.a4b.io')
  }

  // Replace .mp4 with /master.m3u8
  updatedUrl = updatedUrl.replace('.mp4', '/master.m3u8')

  return updatedUrl
}

const convertToMp4Url = (url: string) => {
  if (!url) return ''

  let updatedUrl = url
  if (url.includes('srm-sbox-cdn-g.a4b.io')) {
    updatedUrl = url.replace('srm-sbox-cdn-g.a4b.io', 'srm-sbox-cdn.a4b.io')
  } else if (url.includes('srm-cdn-g.a4b.io')) {
    updatedUrl = url.replace('srm-cdn-g.a4b.io', 'srm-cdn.a4b.io')
  }
  updatedUrl = updatedUrl.replace('/master.m3u8', '.mp4')
  return updatedUrl
}

export const formatCreateTempleApi = (
  formData: any,
  languageState: any,
  isActive: boolean,
  isEditJourney: boolean,
  countryCode: string,
  searchResData: any,
): CreateStore => {
  const closedInfoMedia = formData?.closed_info_media_image.map(
    (item: any) => ({
      media_url: item,
      type: 'image',
    }),
  )
  if (formData?.closed_info_media_video?.length) {
    closedInfoMedia.unshift({
      media_url: formData?.closed_info_media_video?.[0],
      type: 'video',
    })
  }

  const infoMedia = formData?.info_media_image.map((item: any) => ({
    media_url: item,
    type: 'image',
  }))
  if (formData?.info_media_video?.length) {
    infoMedia.unshift({
      media_url: formData?.info_media_video?.[0],
      type: 'video',
    })
  }

  const slug: string = formData?.slug?.replace(/\s+/g, '-')
  const stateOptions = getStatesByLanguage(languageState?.monetisationLanguage)

  const pos_fallback =
    formData?.place_of_supply ||
    stateOptions.find((state: any) => state?.name === formData?.state)?.code
  const formObject: CreateStore = {
    slug: slug,
    title: formData?.title,
    short_description: formData?.short_description,
    long_description: formData?.long_description,
    hub_id: formData?.hub_id,
    icon_url: formData?.icon_url[0],
    cover_media: {
      media_url: formData?.media_url[0],
      type: formData?.coverMediaType,
      media_thumbnail: formData?.thumbnail?.[0] ?? null,
    },
    info_media: infoMedia,
    closed_cover_media: {
      media_url: formData?.closed_media_url[0],
      type: formData?.closedCoverMediaType,
    },
    closed_info_media: closedInfoMedia,
    city: formData?.city,
    state: formData?.state,
    place_of_supply: formData?.place_of_supply || pos_fallback || '',
    id: formData?.id,
    active: isActive,
    service_type: formData?.service_type,
    language_code: languageState.monetisationLanguage,
    location_details: {
      temple_details: {
        name: formData?.location_details?.temple_details?.name,
        location: formData?.location_details?.temple_details?.location,
        description: formData?.location_details?.temple_details?.description,
        image: formData?.location_details?.temple_details?.image?.[0],
      },
      pandit_details: {
        name: formData?.location_details?.pandit_details?.name,
        location: formData?.location_details?.pandit_details?.location,
        description: formData?.location_details?.pandit_details?.description,
        image: formData?.location_details?.pandit_details?.image?.[0],
      },
      show_location_details: formData?.location_details?.show_location_details,
    },
    country_code: countryCode,
    astro_pooja_list: formData?.astro_pooja_list?.length
      ? formData?.astro_pooja_list
      : null,
    tags: formData?.tags?.length ? formData?.tags : null,
    multi_day: formData?.multi_day,
    multi_temple: formData?.multi_temple,
    executions: formData?.executions,
    show_reorder_card_l2: formData?.show_reorder_card_l2 ? true : false,
    metadata: {
      pooja_specificity: formData?.pooja_specificity,
      pooja_usecase: formData?.pooja_usecase,
      pooja_title: formData?.pooja_title,
      cover_media_video: formData?.cover_media_video?.media_url?.[0]
        ? {
            media_url:
              convertToM3U8Url(formData?.cover_media_video?.media_url?.[0]) ||
              null,

            type: 'm3u8',

            play_audio: formData?.cover_media_video?.play_audio,
            media_thumbnails:
              formData?.cover_media_video?.media_thumbnails
                ?.map((thumb: any) => {
                  const url = thumb?.media_url?.[0]
                  return url ? { media_url: url, type: 'image' } : null
                })
                .filter(Boolean) || [],
          }
        : null,
    },
    search_metadata: { ...formData?.search_metadata },
    puja_faq: formData?.enable_puja_faq ? formData?.puja_faq : null,
    mantra_data: formData?.enable_mantra_data
      ? {
          ...formData?.mantra_data,
          icon: formData?.mantra_data?.icon?.[0],
        }
      : null,
    schedule_date: formData?.schedule_date
      ? formatDateToIsoString(formData?.schedule_date)
      : null,
    delivery_date: formData?.delivery_date
      ? formatDateToISTIsoString(formData?.delivery_date)
      : null,
    hindi_calendar_date: formData?.hindi_calendar_date,
    ...(formData?.pickup_address && {
      pickup_address: formData?.pickup_address,
    }),
    sku_code: formData?.sku_code,
    info_bullets: Array.isArray(formData?.info_bullets)
      ? formData.info_bullets.map((item: any) => ({
          text: item?.text ?? item,
        }))
      : [],
  }
  if (formData?.whatsapp_share_media?.[0]) {
    formObject['whatsapp_share_media'] = {
      media_url: formData?.whatsapp_share_media?.[0],
      type: formData?.whatsappMediaType,
    }
  } else {
    formObject['whatsapp_share_media'] = { media_url: '', type: '' }
  }
  return formObject
}
export const parseCreateTempleApiEdit = (
  formData: any,
  languageState: LanguageState,
): CreateStore => {
  const infoMedia = formData?.info_media
    ?.map((item: any) => item.type === 'image' && item.media_url)
    .filter((item: string) => item)

  const video = formData?.info_media.filter(
    (item: any) => item?.type === 'video',
  )
  const closedInfoMedia = formData?.closed_info_media
    ?.map((item: any) => item.type === 'image' && item.media_url)
    .filter((item: string) => item)

  const closedVideo = formData?.closed_info_media?.filter(
    (item: any) => item?.type === 'video',
  )
  const slug: string = formData?.slug?.replace(/\s+/g, '-')
  const formObject: any = {
    store_id: formData?.store_id,
    puja_store_state: formData?.puja_store_state,
    slug: slug,
    title: formData?.title,
    display_title: formData?.display_title,
    short_description: formData?.short_description,
    long_description: formData?.long_description,
    hub_id: formData?.hub_id,
    icon_url: [formData?.icon_url],
    media_url: [formData?.cover_media?.media_url],
    coverMediaType: formData?.cover_media?.type,
    thumbnail: [formData?.cover_media?.media_thumbnail],
    closedCoverMediaType: formData?.closed_cover_media?.type,
    closed_media_url: [formData?.closed_cover_media?.media_url],
    info_media_image: infoMedia,
    closed_info_media_image: closedInfoMedia,
    service_type: formData?.service_type,
    city: formData?.city,
    state: formData?.state,
    place_of_supply: formData?.place_of_supply,
    id: formData?.id,
    schedule_date: formData?.schedule_date
      ? moment(moment(formData?.schedule_date).format('YYYY-MM-DD HH:mm:ss'))
      : undefined,
    delivery_date: formData?.delivery_date
      ? moment(moment(formData?.delivery_date).format('YYYY-MM-DD HH:mm:ss'))
      : undefined,
    hindi_calendar_date: formData?.hindi_calendar_date,
    whatsapp_share_media: formData?.whatsapp_share_media?.media_url
      ? [formData?.whatsapp_share_media?.media_url]
      : null,
    whatsappMediaType: formData?.whatsapp_share_media?.type
      ? formData?.whatsapp_share_media?.type
      : null,
    language_code: languageState.monetisationLanguage,
    location_details: {
      temple_details: {
        ...formData?.location_details?.temple_details,
        image: [formData?.location_details?.temple_details?.image],
      },
      pandit_details: {
        ...formData?.location_details?.pandit_details,
        image: [formData?.location_details?.pandit_details?.image],
      },
      show_location_details: formData?.location_details?.show_location_details,
    },
    country_code: formData?.country_code,
    astro_pooja_list: formData?.astro_pooja_list?.length
      ? formData?.astro_pooja_list
      : [],
    tags: formData?.tags?.length ? formData?.tags : [],
    multi_day: formData?.multi_day,
    multi_temple: formData?.multi_temple,
    executions: formData?.executions,
    show_reorder_card_l2: formData?.show_reorder_card_l2 ? true : false,
    pooja_specificity: formData?.pooja_specificity,
    pooja_usecase: formData?.pooja_usecase,
    pooja_title: formData?.pooja_title,
    puja_faq: formData?.puja_faq,
    enable_puja_faq: formData?.puja_faq ? true : false,
    enable_mantra_data: formData?.mantra_data ? true : false,
    mantra_data: {
      ...formData?.mantra_data,
      icon: [formData?.mantra_data?.icon],
    },
    pickup_address: formData?.pickup_address,
    sku_code: formData?.sku_code,
    search_metadata: formData?.search_metadata,
    info_bullets:
      formData?.info_bullets &&
      Array.isArray(formData?.info_bullets) &&
      formData?.info_bullets.length > 0
        ? formData?.info_bullets
        : [{ text: '' }],
    cover_media_video: {
      // Convert M3U8 to MP4 and wrap in an array
      media_url: formData?.cover_media_video?.media_url
        ? [convertToMp4Url(formData?.cover_media_video?.media_url)]
        : [],

      // Set the play_audio flag
      play_audio: formData?.cover_media_video?.play_audio,

      // Map thumbnails and wrap each URL in an array
      media_thumbnails: (
        formData?.cover_media_video?.media_thumbnails || []
      ).map((thumb: any) => ({
        media_url: thumb.media_url ? [thumb?.media_url] : [],
      })),
    },
  }
  if (formData?.closed_cover_media?.media_url) {
    formObject['closed_media_url'] = [formData?.closed_cover_media?.media_url]
  }
  if (video?.[0]?.media_url) {
    formObject['info_media_video'] = [video?.[0]?.media_url]
  }
  if (closedVideo?.[0]?.media_url) {
    formObject['closed_info_media_video'] = [closedVideo?.[0]?.media_url]
  }
  return formObject
}
export const parseCreateTempleApi = (formData: any) => {
  const formObject = {
    title: formData?.title,
    sub_title: formData?.sub_title,
    short_description: formData?.short_description,
    long_description: formData?.long_description,
    hub_id: formData?.hub_id,
    icon_url: [formData?.icon_url],
    media_url: [formData?.cover_media?.media_url],
    coverMediaType: formData?.cover_media?.type,
    thumbnail: [formData?.cover_media?.thumbnail],
    cta: JSON.stringify(formData?.cta),
    item_image_url: [formData?.item_image_url],
    info_media_image: formData?.info_media_image
      ? [formData?.info_media_image]
      : [],
    info_media_video: formData?.info_media_video
      ? [formData?.info_media_video]
      : [],
    city: formData?.city,
    state: formData?.state,
  }
  return formObject
}

const parseAdditionalInfo = (additionInfo: string) => {
  const snippet = additionInfo
  const parser = new DOMParser()
  const doc = parser.parseFromString(snippet, 'text/html')
  const spans = doc.querySelectorAll('span.item span')
  const images = doc.querySelectorAll('img')

  const list = Array.from(spans).map(span => span.innerHTML.trim())
  const iconImg = [Array.from(images).map(img => img.getAttribute('src'))?.[0]]

  return list.length <= 0
    ? { error: true, additionInfo, iconImg }
    : { list, iconImg }
}

export const parseStoreProductList = (
  formData: ProductData,
  campaignList: any,
  storeList: any,
  languageState: LanguageState,
) => {
  let campaignArray: any[] = []
  campaignList?.map((campaign: any) =>
    campaignArray.push([campaign?.id, campaign.name]),
  )
  const stores = storeList?.map((store: any) => ({
    label: store?.title,
    value: store?.id,
  }))
  const productList: any = formData?.products
    ?.map((item: Product) => ({
      id: item?.id,
      title: item?.title,
      description: item?.description,
      image_url: [item?.image_url],
      // bottomsheet_image_url: [item?.bottomsheet_image_url],
      price: item?.price,
      master_product_id: item?.master_product_id,
      position: item?.position || 0,
      active: item?.active,
      batch_size: item?.batch_size || 20,
      short_name: item?.short_name,
      quantities: item?.quantities || 0,
      additional_info:
        item?.additional_info_v2 && item?.bullet_icon_url
          ? { list: item?.additional_info_v2, iconImg: [item?.bullet_icon_url] }
          : parseAdditionalInfo(item?.additional_info || ''),

      show_additional_info: item?.show_additional_info,
      member_size: item?.member_size ? item?.member_size : null,
      item_type: item?.item_type,
      language_code: item?.language_code,
      multi_add: item?.multi_add,
      specificity: item?.specificity,
      country_code: item?.country_code,
      currency: item?.currency,
      bestseller: item?.bestseller,
      deliverable: item?.deliverable,
      process_date: moment(
        moment(item?.process_date).format('YYYY-MM-DD HH:mm:ss'),
      ).utcOffset(0),
      campaignList: item?.campaigns
        ?.map((id: any) =>
          campaignList
            ?.filter((obj: any) => obj?.id === id)
            ?.map((obj: any) => obj?.name),
        )
        ?.flat(),
      campaignArray: campaignArray,
      storeList: stores,
      recommended: item?.recommended,
      exp_linked: item?.exp_linked,
      pickup_address: item?.pickup_address,
      sku_code: item?.sku_code,
      bottomsheet_medias:
        item?.sub_type === 'chadava'
          ? [item?.bottomsheet_image_url]
          : item?.bottomsheet_medias?.map(
              (item: { media_url: string; media_type: string }) =>
                item?.media_url,
            ),
      is_igst: item?.is_igst,
      hsn_code: item?.hsn_code,
      tax_rate: item?.tax_rate,

      origin_store_id: item?.origin_store_id || null,
      offering_sku_id: item?.offering_sku_id || null,
      sub_type: (item?.sub_type === 'chadava' && 'chadava') || null,
      chadava_subtype: (item?.sub_type && 'chadhava') || null,
      origin_hub_name: item?.origin_hub_name || null,
      origin_temple_name: item?.origin_temple_name || null,
      place_of_supply: item?.place_of_supply || null,
      info_bullets:
        item?.info_bullets &&
        Array.isArray(item?.info_bullets) &&
        item?.info_bullets.length > 0
          ? item?.info_bullets
          : [{ text: '' }],
      recommended_skus:
        item?.recommended_skus &&
        Array.isArray(item?.recommended_skus) &&
        item?.recommended_skus.length > 0
          ? item?.recommended_skus
          : [],
    }))
    .sort((item_1: any, item_2: any) => item_1?.position - item_2?.position)
  return productList
}
export const formatAssignItemsApi = (formData: any): CreateInventory => {
  const inventoryList = formData.added.map((item: any, index: number) => ({
    master_product_id: item.id,
    name: item.title,
    image_url: item.image_url,
    // bottomsheet_image_url: item.bottomsheet_image_url,
    description: item.description,
    price: item.price,
    quantities: 0,
    position: index,
    multi_add: item.multi_add || false,
    bestseller: false,
    cta: {
      type: 'whatsapp_share',
      color: 'green',
      text: 'Add to cart',
    },
    active: true,
    type: item.type,
    specificity: item.specificity,
  }))
  return { products: inventoryList }
}
export const formatFaqsAndreviewsApi = (
  formData: any,
  storeId: string,
  storeSession: StoreSessionDay[] | [],
): any => {
  //Current changes
  const formObject: any = {}
  formObject['faqs'] = {
    create: {
      faq_type: 'store',
      entity_id: storeId,
      faqs: formData?.faqs
        ?.filter((item: any) => !item?.id)
        ?.map((item: any, index: number) => ({
          question: item?.question,
          answer: item?.answer,
          position: item?.position,
          active: true,
        })),
    },
    update: {
      faq_type: 'store',
      entity_id: storeId,
      faqs: formData?.faqs
        ?.filter((item: any) => item?.id)
        ?.map((item: any, index: number) => ({
          id: item?.id,
          question: item?.question,
          answer: item?.answer,
          position: item?.position,
          active: true,
        })),
    },
  }
  formObject['reviews'] = {
    create: {
      store_id: storeId,
      reviews: formData?.reviews
        ?.filter((item: any) => !item?.id)
        ?.map((item: any, index: number) => ({
          review: item?.review,
          user_name: item?.user_name,
          user_image_url: item?.user_image_url?.[0],
          position: item?.position,
          rating: item?.rating,
        })),
    },
    update: {
      store_id: storeId,
      reviews: formData?.reviews
        ?.filter((item: any) => item?.id)
        ?.map((item: any, index: number) => ({
          id: item?.id,
          review: item?.review,
          user_name: item?.user_name,
          user_image_url: item?.user_image_url?.[0],
          position: item?.position,
          rating: item?.rating,
        })),
    },
  }
  formObject['pooja_benefits'] = {
    create: {
      page_type: 'pooja_benefits',
      entity_id: storeId,
      steps: formData?.pooja_benefits
        ?.filter((item: any) => !item?.id)
        ?.map((item: any, index: number) => ({
          step: item?.step,
          icon: item?.pooja_benefit_icon?.[0],
          description: item?.description,
          position: item?.position,
        })),
    },
    update: {
      page_type: 'pooja_benefits',
      entity_id: storeId,
      steps: formData?.pooja_benefits
        ?.filter((item: any) => item?.id)
        ?.map((item: any, index: number) => ({
          id: item?.id,
          step: item?.step,
          icon: item?.pooja_benefit_icon?.[0],
          description: item?.description,
          position: item?.position,
        })),
    },
  }
  formObject['pooja_process'] = {
    create: {
      page_type: 'pooja_process',
      entity_id: storeId,
      steps: formData?.pooja_process
        ?.filter((item: any) => !item?.id)
        ?.map((item: any, index: number) => ({
          step: item?.step,
          icon: item?.pooja_process_icon?.[0],
          description: item?.description,
          position: item?.position,
        })),
    },
    update: {
      page_type: 'pooja_process',
      entity_id: storeId,
      steps: formData?.pooja_process
        ?.filter((item: any) => item?.id)
        ?.map((item: any, index: number) => ({
          id: item?.id,
          step: item?.step,
          icon: item?.pooja_process_icon?.[0],
          description: item?.description,
          position: item?.position,
        })),
    },
  }

  const days = [0, 1, 2, 3, 4, 5, 6]
  const storeSessions = days?.map((day: any, index: number) => ({
    weekday: index,
    opening_hour: formData?.session?.[0]?.format('HH:mm'),
    closing_hour: formData?.session?.[1]?.format('HH:mm'),
    id: storeSession?.[index]?.id,
  }))

  return {
    faqs: { faqs: formObject['faqs'] },
    reviews: { reviews: formObject['reviews'] },
    pooja_benefits: {
      pooja_benefits: formObject['pooja_benefits'],
    },
    pooja_process: {
      pooja_process: formObject['pooja_process'],
    },
    status: formData.status,
    session: {
      sessions: storeSessions,
    },
  }
}
export const formatInventoryForEditAdd = (
  formData: any,
  countryCode: string,
  languageCode: string,
  type: string,
) => {
  return {
    master_product_id: formData?.master_product_id,
    title: formData?.title,
    image_url: formData?.image_url,
    // bottomsheet_image_url: formData?.bottomsheet_image_url,
    description: formData?.description,
    price: formData?.price,
    archived: formData?.archived,
    item_type: formData?.item_type,
    multi_add: formData?.multi_add,
    bestseller: false,
    active: formData?.active || false,
    position: formData?.position || 0,
    quantities: formData?.quantities || 0,
    cta: {
      type: 'redirection',
      color: 'green',
      text: 'Add to cart',
    },
    is_igst: formData?.is_igst || false,
    hsn_code: formData?.hsn_code || null,
    tax_rate:
      formData?.tax_rate === '' || formData?.tax_rate == null
        ? null
        : Number(formData?.tax_rate),
    language_code: languageCode,
    currency: countryCode !== 'IN' ? 'USD' : 'INR',
    country_code: countryCode,
    bottomsheet_medias: formData?.bottomsheet_medias?.map((item: any) => ({
      media_type: 'image',
      media_url: item?.media_url,
    })),
    specificity: formData?.specificity,
    additional_info_v2: formData?.additional_info_v2,
    bullet_icon_url: formData?.bullet_icon_url,
    member_size: formData?.member_size,
    deliverable: formData.deliverable,
    short_name: formData.short_name,
    ...(type === 'chadhava' && {
      origin_store_id: formData?.store_id,
      offering_sku_id: formData?.id,
      sub_type: 'chadava',
      chadava_subtype: formData?.sub_type,
      origin_hub_name: formData?.origin_hub_name,
      origin_temple_name: formData?.origin_temple_name,
      place_of_supply: formData?.place_of_supply,
      bottomsheet_image_url: formData?.bottomsheet_image_url,
    }),
    batch_size: formData?.batch_size || DEFAULT_BATCH_SIZE,
  }
}

export const formatInventoryForEdit = (
  formData: any,
  languageCode: string,
  countryCode: string,
  campaignList?: any,
  serviceType?: string,
  storeType?: string,
) => {
  const selectedCampaignIds = formData?.campaignList
    ?.map((name: any) =>
      campaignList
        ?.filter((obj: any) => obj?.name === name)
        ?.map((obj: any) => obj?.id),
    )
    ?.flat()

  const divOpeningTag = `<div style="display: flex; flex-direction: column">`
  const divClosingTag = `</div>`
  const dynamicValues = formData.additional_info?.list
    ?.map(
      (info: string, index: number, array: string[]) =>
        `<span style="display: flex; flex-direction: row; align-items: start; width: 100%;" class="item"><img width="24" height="24" src=${
          formData?.additional_info?.iconImg?.[0]
        } /><span>${info}</span></span>${
          index !== array.length - 1 ? '<br />' : ''
        }`,
    )
    .join('')
  let result = divOpeningTag.concat(dynamicValues, divClosingTag)

  let payload: CreateInventoryPayload = {
    title: formData?.title,
    image_url: formData?.image_url?.[0],
    // bottomsheet_image_url: formData?.bottomsheet_image_url?.[0],
    description: formData?.description,
    price: formData?.price,
    archived: formData?.archived,
    item_type: formData?.item_type,
    multi_add: formData?.multi_add,
    bestseller: formData?.bestseller || false,
    deliverable: formData?.deliverable,
    active: formData?.active,
    quantities: formData?.quantities || 0,
    position: formData?.position || 0,
    batch_size: formData?.batch_size,
    short_name: formData.short_name,
    language_code: languageCode,
    campaigns: selectedCampaignIds,
    country_code: countryCode,
    currency: formData?.currency,
    additional_info_v2: formData.additional_info?.list,
    show_additional_info: formData?.show_additional_info || false,
    bullet_icon_url: formData?.additional_info?.iconImg?.[0],
    ...(formData?.item_type === 'pooja' && {
      process_date: formatDateToIsoString(formData?.process_date) || '',
      member_size: formData?.member_size,
      additional_info: result,
      specificity: formData?.specificity,
      recommended: formData?.recommended,
    }),
    ...(formData?.pickup_address && {
      pickup_address: formData?.pickup_address,
    }),
    sku_code: formData?.sku_code,
    bottomsheet_medias: formData?.bottomsheet_medias?.map((item: string) => {
      const fileExtension = getMediaExtension(item)
      const mediaType = fileExtension ? getFileFormat(fileExtension) : 'image'
      return {
        media_type: mediaType,
        media_url: item,
      }
    }),
    is_igst: formData?.is_igst || false,
    hsn_code: formData?.hsn_code || null,
    tax_rate:
      formData?.tax_rate === '' || formData?.tax_rate == null
        ? null
        : Number(formData?.tax_rate),

    origin_store_id: formData?.origin_store_id || null,
    offering_sku_id: formData?.offering_sku_id || null,
    origin_hub_name: formData?.origin_hub_name || null,
    origin_temple_name: formData?.origin_temple_name || null,
    place_of_supply: formData?.place_of_supply || null,
    info_bullets: Array.isArray(formData?.info_bullets)
      ? formData.info_bullets
      : [],
    recommended_skus:
      Array.isArray(formData?.recommended_skus) &&
      formData.recommended_skus.length > 0
        ? formData.recommended_skus
        : null,
    sub_type:
      ((formData?.sub_type === 'chadava' ||
        (formData?.origin_store_id && formData?.offering_sku_id)) &&
        'chadava') ||
      null,
    chadava_subtype:
      ((formData?.sub_type === 'chadava' ||
        (formData?.origin_store_id && formData?.offering_sku_id)) &&
        'chadhava') ||
      null,
  }
  if (serviceType === 'pooja' && formData?.item_type === 'item') {
    delete payload?.campaigns
  }

  return payload
}

export const getDatesFromConfig = (config: any) => {
  const { interval, num_of_days, start_date, weekday, custom_dates } = config
  const dates: moment.Moment[] = []
  if (!start_date) return dates

  const startDateMoment = moment(start_date)

  if (interval === 'custom') {
    if (custom_dates && Array.isArray(custom_dates)) {
      // Sort dates to ensure increasing sequence
      const sortedDates = [...custom_dates].sort(
        (a, b) => a.valueOf() - b.valueOf(),
      )
      dates.push(...sortedDates)
    }
  } else if (interval === 'daily') {
    for (let i = 0; i < num_of_days; i++) {
      dates.push(moment(startDateMoment).add(i, 'days'))
    }
  } else if (interval === 'weekly') {
    // Find first occurrence of weekday on or after start_date
    let current = moment(startDateMoment)
    if (weekday !== undefined && weekday !== null) {
      while (current.day() !== weekday) {
        current.add(1, 'days')
      }
    }
    for (let i = 0; i < num_of_days; i++) {
      dates.push(moment(current))
      current.add(1, 'weeks')
    }
  } else if (interval === 'monthly') {
    for (let i = 0; i < num_of_days; i++) {
      dates.push(moment(startDateMoment).add(i, 'M'))
    }
  }
  return dates
}

export const createAddDateHandler = (
  value: moment.Moment[],
  onChange: ((val: moment.Moment[]) => void) | undefined,
  maxDates: number | undefined,
) => {
  return () => {
    if (maxDates && value.length >= maxDates) {
      return { error: `You can only add up to ${maxDates} dates` }
    }
    const lastDate = value.length > 0 ? value[value.length - 1] : moment()
    const newDate =
      value.length > 0 ? moment(lastDate).add(1, 'days') : lastDate
    const newValue = [...value, newDate]
    onChange?.(newValue)
    return { success: true }
  }
}

export const createRemoveDateHandler = (
  value: moment.Moment[],
  onChange: ((val: moment.Moment[]) => void) | undefined,
) => {
  return (index: number) => {
    const newValue = [...value]
    newValue.splice(index, 1)
    onChange?.(newValue)
  }
}

export const createUpdateDateHandler = (
  value: moment.Moment[],
  onChange: ((val: moment.Moment[]) => void) | undefined,
) => {
  return (index: number, date: moment.Moment | null) => {
    if (!date) return
    const newValue = [...value]
    newValue[index] = date
    onChange?.(newValue)
  }
}

export const isDateDisabled = (current: moment.Moment) => {
  return current && current < moment().startOf('day')
}

export const parseExecutionsToFormData = (
  executionsData: any,
  multiTempleVal: boolean,
) => {
  const extractedTempleIds = Array.from(
    new Set(
      (executionsData.executions || [])
        .map((ex: any) => ex.temple_id)
        .filter(Boolean),
    ),
  )

  const existingExecutions = executionsData.executions || []
  const dateTemplesMap: Record<number, string[]> = {}

  existingExecutions.forEach((ex: any) => {
    const index = (ex.day || 1) - 1
    if (index >= 0 && ex.temple_id) {
      if (!dateTemplesMap[index]) {
        dateTemplesMap[index] = []
      }
      if (!dateTemplesMap[index].includes(ex.temple_id)) {
        dateTemplesMap[index].push(ex.temple_id)
      }
    }
  })

  const dateTemplesArray: any[] = []
  Object.keys(dateTemplesMap).forEach((key: any) => {
    if (multiTempleVal) {
      dateTemplesArray[key] = dateTemplesMap[key]
    } else {
      dateTemplesArray[key] = dateTemplesMap[key]?.[0]
    }
  })

  return {
    extractedTempleIds,
    dateTemplesArray,
  }
}

export const buildTempleConfigDrawerFormValues = (params: {
  executionsData: any
  multiDayVal: boolean
  multiTempleVal: boolean
}) => {
  const { executionsData, multiDayVal, multiTempleVal } = params

  const { extractedTempleIds, dateTemplesArray } = parseExecutionsToFormData(
    executionsData,
    multiTempleVal,
  )

  return {
    multi_day: multiDayVal,
    multi_temple: multiTempleVal,
    interval: executionsData.interval || 'daily',
    num_of_days: executionsData.num_of_days || 1,
    start_date: executionsData.start_date
      ? moment(executionsData.start_date)
      : moment(),
    weekday: executionsData.weekday,
    custom_dates:
      executionsData.custom_dates?.map((d: string) => moment(d)) || [],
    temple_ids: extractedTempleIds.length > 0 ? extractedTempleIds : [],
    date_temples: dateTemplesArray,
  }
}

export const convertTempleValues = (
  currentValues: any[],
  multiTemple: boolean,
) => {
  if (!currentValues || !Array.isArray(currentValues)) return null

  return currentValues.map((val: any) => {
    if (multiTemple) {
      if (Array.isArray(val)) return val
      return val ? [val] : []
    } else {
      if (Array.isArray(val)) return val[0]
      return val
    }
  })
}

export const copyTemplesToAllDays = (
  firstDayTemples: any,
  previewDatesLength: number,
) => {
  const updates: any = {}
  for (let i = 1; i < previewDatesLength; i++) {
    updates[`date_temples[${i}]`] = firstDayTemples
  }
  return Object.keys(updates).map(key => ({
    name: ['date_temples', parseInt(key.match(/\d+/)?.[0] || '0')],
    value: updates[key],
  }))
}

export const getNextCopyToAllDaysValue = (params: {
  prevMultiTemple?: boolean
  multiTemple: boolean
  prevPreviewDatesLength: number
  previewDatesLength: number
  copyToAllDays: boolean
}): boolean => {
  const {
    prevMultiTemple,
    multiTemple,
    prevPreviewDatesLength,
    previewDatesLength,
    copyToAllDays,
  } = params

  if (previewDatesLength <= 1) return false

  const switchedToSingleTemple = prevMultiTemple === true && !multiTemple
  const datesBecameMultiple =
    prevPreviewDatesLength <= 1 && previewDatesLength > 1

  // Default ON for single-temple mode when we first enter it
  // (or when dates become > 1). Otherwise keep current state.
  if (
    !multiTemple &&
    (prevMultiTemple === undefined ||
      switchedToSingleTemple ||
      datesBecameMultiple)
  ) {
    return true
  }

  // Default OFF when switching into multi-temple mode.
  if (multiTemple && prevMultiTemple === false) {
    return false
  }

  return copyToAllDays
}

export const generateExecutions = (
  values: any,
  getDatesFromConfig: (config: any) => moment.Moment[],
  mainHubId: string,
) => {
  const { multi_temple, temple_ids, date_temples } = values
  const dates = getDatesFromConfig(values)
  const executions: any[] = []

  dates.forEach((date: moment.Moment, index: number) => {
    const dayNum = index + 1
    let templesToUse: string[] = []

    if (multi_temple) {
      if (date_temples && date_temples[index]) {
        const val = date_temples[index]
        if (Array.isArray(val) && val.length > 0) {
          templesToUse = val
        }
      } else if (temple_ids && temple_ids.length > 0) {
        templesToUse = temple_ids
      }
    } else {
      if (date_temples && date_temples[index]) {
        const val = date_temples[index]
        if (typeof val === 'string') {
          templesToUse = [val]
        } else if (Array.isArray(val) && val.length > 0) {
          templesToUse = [val[0]]
        }
      }

      if (templesToUse.length === 0 && mainHubId) {
        templesToUse = [mainHubId]
      }
    }

    if (templesToUse.length > 0) {
      templesToUse.forEach(tid => {
        executions.push({
          day: dayNum,
          id: uuidv4(),
          start_time: date.toISOString(),
          temple_id: tid,
        })
      })
    } else {
      executions.push({
        day: dayNum,
        id: uuidv4(),
        start_time: date.toISOString(),
        temple_id: undefined,
      })
    }
  })

  return executions
}

export const buildStoreFormValuesFromTempleConfigDrawer = (params: {
  values: any
  getDatesFromConfig: (config: any) => moment.Moment[]
  mainHubId: string
}) => {
  const { values, getDatesFromConfig, mainHubId } = params
  const executions = generateExecutions(values, getDatesFromConfig, mainHubId)

  return {
    multi_day: values.multi_day,
    multi_temple: values.multi_temple,
    executions: {
      executions,
      interval: values.interval,
      num_of_days: values.num_of_days,
      start_date: values.start_date.toISOString(),
      weekday: values.weekday,
      custom_dates:
        values.interval === 'custom'
          ? values.custom_dates.map((d: any) =>
              moment.utc(d.format('YYYY-MM-DD')).toISOString(),
            )
          : null,
    },
  }
}
